import Bugsnag from "@bugsnag/js";
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import localforage from "localforage";
import MixPanel from "mixpanel-browser";

import axiosInstance, {
  storeAuthTokensToSecureStore,
} from "../../axiosInstance";
import { app } from "../../firebaseInstance";
import {
  handleTypingStatusOnAuth,
  logToFirebase,
  trackEvent,
} from "../../helperFunctions";
import URL from "../../urls";
import { TYPE_PSYCHIATRIST, TYPE_THERAPIST } from "../../variable";
import {
  ADD_TOAST,
  AUTH_ERROR,
  AUTH_LOADING,
  GAUTH_CREDS,
  LOGIN,
  LOGOUT,
  RESET_AUTH,
  SET_WS_PARAMS,
} from "./types";

// Create a singleton auth instance
const auth = getAuth(app);

export const resetAuth = () => ({ type: RESET_AUTH });

export const loginUser = userData => {
  return async dispatch => {
    dispatch({ type: AUTH_LOADING, payload: true });
    dispatch({ type: AUTH_ERROR, payload: "" });
    await axiosInstance
      .post(URL.login, userData)
      .then(async response => {
        await storeAuthTokensToSecureStore(response.headers);
        let user = response.data.data;
        MixPanel.identify(user.firebaseid);
        if (
          user.usertype === TYPE_THERAPIST ||
          user.usertype === TYPE_PSYCHIATRIST
        ) {
          loginFirebase(user, dispatch);
          trackEvent({
            event: "twa_login_click",
            payload: {
              auth_status: "success",
              provider_name: `${user?.firstname} ${user?.lastname}`,
              provider_id: user?.id,
              provider_type: user?.usertype,
              provider_uuid: user?.uuid,
              is_cf_provider: user?.is_cf_provider,
            },
          });
        } else {
          dispatch({ type: AUTH_ERROR, payload: "Invalid User!" });
          dispatch({ type: AUTH_LOADING, payload: false });
          trackEvent({
            event: "twa_login_click",
            payload: { auth_status: "failure" },
          });
        }

        dispatch({
          type: SET_WS_PARAMS,
          payload: {
            access_token: response.headers["access-token"],
            client: response.headers.client,
            uid: response.headers.uid,
          },
        });
      })
      .catch(error => {
        console.log("Login Response Error", error);
        dispatch({ type: AUTH_ERROR, payload: error });
        dispatch({ type: AUTH_LOADING, payload: false });
        trackEvent({
          event: "twa_login_click",
          payload: { auth_status: "failure" },
        });
      });
  };
};

export const loginFirebase = (authObj, dispatch) => {
  if (!authObj.fb_token) {
    Bugsnag.notify("Firebase Token not found", authObj);
    return;
  }

  signInWithCustomToken(auth, authObj.fb_token).catch(error => {
    dispatch({
      type: AUTH_ERROR,
      payload: error?.message || "Firebase Login Failed!",
    });
    dispatch({ type: AUTH_LOADING, payload: false });
    Bugsnag.notify(error);
    logout();
  });
  onAuthStateChanged(auth, user => {
    if (user) {
      handleTypingStatusOnAuth(user.uid);
      dispatch({ type: LOGIN, payload: authObj });
    }
  });
};

export const updateProfile = data => {
  return async dispatch => {
    dispatch({
      type: LOGIN,
      payload: data,
    });
  };
};

export const refreshAuthToken = () => {
  return dispatch => {
    axiosInstance.post(URL.tokenRefresh).catch(err => {
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: `Couldn't refresh the token ,${err}.`,
          appearance: "error",
          autoDismiss: false,
        },
      });
    });
  };
};

export const logoutFirebase = async () => {
  try {
    const user = auth.currentUser;
    handleTypingStatusOnAuth(user?.uid);
    await signOut(auth);
  } catch (err) {
    Bugsnag.notify(err);
  }
};

export const logout = () => async dispatch => {
  try {
    await axiosInstance.delete(URL.logout);
  } catch (err) {
    Bugsnag.notify(err);
  } finally {
    try {
      const tourShown = JSON.parse(localStorage.getItem("tourShown"));
      const aiPermissionModalShown = JSON.parse(localStorage.getItem("aiPermissionModalShown"));
      await logoutFirebase();
      dispatch({ type: LOGOUT });
      localforage.clear();
      sessionStorage.clear();
      localStorage.clear();
      localStorage.setItem("tourShown", JSON.stringify(tourShown));
      localStorage.setItem("aiPermissionModalShown", JSON.stringify(aiPermissionModalShown));
      window.location.href = "/login";
    } catch (error) {
      Bugsnag.notify(error);
    }
  }
};

export const authenticateUsingJWT = async (dispatch, jwt_token, onSuccess) => {
  try {
    logToFirebase("authenticateUsingJWT called");

    if (!jwt_token) {
      throw new Error("JWT Token not found!");
    }

    dispatch({ type: AUTH_LOADING, payload: true });
    dispatch({ type: AUTH_ERROR, payload: null });

    await localforage.clear();

    const response = await axiosInstance.get(URL.decodeSecureToken, {
      headers: { "jwt-token": jwt_token },
    });
    storeAuthTokensToSecureStore(response.headers);

    if (response?.data?.user) {
      let user = response.data.user;
      MixPanel.identify(user.firebaseid);
      if ([TYPE_THERAPIST, TYPE_PSYCHIATRIST].includes(user.usertype)) {
        loginFirebase(user, dispatch);
      } else {
        dispatch({ type: AUTH_ERROR, payload: "Invalid User!" });
        dispatch({ type: AUTH_LOADING, payload: false });
      }

      dispatch({
        type: SET_WS_PARAMS,
        payload: {
          access_token: response.headers["access-token"],
          client: response.headers.client,
          uid: response.headers.uid,
        },
      });

      onSuccess();
      logToFirebase("User authenticated using JWT token");
    } else {
      throw new Error(`Error authenticating user! ${jwt_token}`);
    }
  } catch (error) {
    let errorMessage = `Authentication error, please log out.`;
    if (error?.response?.status === 401) {
      errorMessage = "Invalid token, please log out.";
    }

    dispatch({ type: AUTH_LOADING, payload: false });
    dispatch({ type: AUTH_ERROR, payload: errorMessage });

    Bugsnag.notify(error, event => {
      // Add searchable metadata
      event.addMetadata("auth", {
        message: "Authenticating user using JWT token",
        jwt_token,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
      });

      event.context = "Authentication failure with jwt token";
    });

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("openProfileBottomSheet");
    }
  }
};

export const fetchGauthCreds = () => async dispatch => {
  try {
    const res = await axiosInstance.get(URL.gauth);

    dispatch({
      type: GAUTH_CREDS,
      payload: {
        clientId: res?.data?.client_id,
        clientSecret: res?.data?.client_secret,
      },
    });
  } catch (err) {
    Bugsnag.notify(err);
  }
};
